import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useControl } from 'react-map-gl/mapbox'
import type { ControlPosition, IControl, MapInstance } from 'react-map-gl/mapbox'

const EXPAND_ICON =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>'

const COLLAPSE_ICON =
  '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>'

type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => void
}

type FullscreenCapableDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => void
}

function getFullscreenElement(): Element | null {
  const doc = document as FullscreenCapableDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null
}

type ControlLabels = {
  enterFullscreen: string
  exitFullscreen: string
  enlargeMap: string
}

class MapFullscreenControlImpl implements IControl {
  private container: HTMLDivElement
  private button: HTMLButtonElement
  private map?: MapInstance
  private fullscreenTarget?: FullscreenCapableElement
  private labels: ControlLabels
  private getOnEnlarge: () => (() => void) | undefined

  constructor(labels: ControlLabels, getOnEnlarge: () => (() => void) | undefined) {
    this.labels = labels
    this.getOnEnlarge = getOnEnlarge
    this.container = document.createElement('div')
    this.container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    this.button = document.createElement('button')
    this.button.type = 'button'
    this.button.style.alignItems = 'center'
    this.button.style.color = '#3f4754'
    this.button.style.display = 'flex'
    this.button.style.justifyContent = 'center'

    this.container.append(this.button)
  }

  onAdd(map: MapInstance): HTMLElement {
    this.map = map
    this.fullscreenTarget = map.getContainer() as FullscreenCapableElement
    this.button.addEventListener('click', this.handleClick)
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    this.updateButton()
    return this.container
  }

  onRemove(): void {
    this.button.removeEventListener('click', this.handleClick)
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange)
    this.container.remove()
    this.map = undefined
  }

  private handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()

    const onEnlarge = this.getOnEnlarge()
    if (onEnlarge) {
      onEnlarge()
      return
    }

    const target = this.fullscreenTarget
    if (!target) {
      return
    }

    const doc = document as FullscreenCapableDocument
    const result = this.isFullscreen()
      ? (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc)
      : (target.requestFullscreen ?? target.webkitRequestFullscreen)?.call(target)

    // requestFullscreen/exitFullscreen return a promise that can reject when
    // the environment blocks fullscreen (e.g. a sandboxed iframe); swallow it
    // so it does not surface as an unhandled rejection.
    Promise.resolve(result).catch(() => {})
  }

  private handleFullscreenChange = () => {
    this.updateButton()
    this.map?.resize()
  }

  private isFullscreen(): boolean {
    return Boolean(this.fullscreenTarget) && getFullscreenElement() === this.fullscreenTarget
  }

  private updateButton() {
    // In enlarge mode the button opens the main trip map rather than browser
    // fullscreen, so it always shows the expand icon and its own label.
    if (this.getOnEnlarge()) {
      this.button.innerHTML = EXPAND_ICON
      this.button.setAttribute('aria-label', this.labels.enlargeMap)
      this.button.title = this.labels.enlargeMap
      return
    }

    const isFullscreen = this.isFullscreen()
    const label = isFullscreen ? this.labels.exitFullscreen : this.labels.enterFullscreen

    this.button.innerHTML = isFullscreen ? COLLAPSE_ICON : EXPAND_ICON
    this.button.setAttribute('aria-label', label)
    this.button.title = label
  }

  setLabels(labels: ControlLabels) {
    this.labels = labels
    this.updateButton()
  }

  refresh() {
    this.updateButton()
  }
}

type Props = {
  position?: ControlPosition
  /**
   * When provided, the button opens the main trip map (via this callback)
   * instead of toggling browser fullscreen.
   */
  onEnlarge?: () => void
}

function MapFullscreenControl({ position = 'top-right', onEnlarge }: Props) {
  const intl = useIntl()
  const onEnlargeRef = useRef(onEnlarge)
  const labels = useMemo(() => ({
    enterFullscreen: intl.formatMessage({
      id: 'LHZXWNGH_gDwVLRZj0UP',
      defaultMessage: 'Open full screen map',
      description: 'Accessible label for the Trip Planner map control that expands the map to full screen',
    }),
    exitFullscreen: intl.formatMessage({
      id: 'gsrL42TDfiLY8PSlT08e',
      defaultMessage: 'Exit full screen map',
      description: 'Accessible label for the Trip Planner map control that closes the full screen map view',
    }),
    enlargeMap: intl.formatMessage({
      id: 'NYiUMCT_1zOyHIC0vEBe',
      defaultMessage: 'Enlarge map',
      description: 'Accessible label for the Trip Planner map control that opens the location on the main trip map',
    }),
  }), [intl])

  const control = useControl(
    () => new MapFullscreenControlImpl(labels, () => onEnlargeRef.current),
    { position },
  )

  useEffect(() => {
    onEnlargeRef.current = onEnlarge
    // Enlarge mode toggles the icon/label; refresh in case onEnlarge resolves
    // after the control was first added.
    control.refresh()
  }, [control, onEnlarge])

  useEffect(() => {
    control.setLabels(labels)
  }, [control, labels])

  return null
}

export default MapFullscreenControl
